/**
 * Die Marke an der zuletzt geöffneten Zeile.
 *
 * Wer aus einer Liste heraus etwas öffnet und zurückkommt, sucht als Erstes die
 * Zeile, an der er war — bei fünfundzwanzig fast gleich aussehenden Zeilen ist
 * das die eigentliche Arbeit an der Rückkehr. Die Marke nimmt sie ab: Sie steht
 * vorn in der Zeile und holt sie zugleich in den sichtbaren Bereich.
 *
 * Sie steht in einer eigenen, schmalen ersten Spalte und nicht als Färbung der
 * Zeile. Der Baustein führt seit #142 zwar eine Zeilentönung (`tone`), aber die
 * meint etwas anderes: grün ok, amber offen, rot überfällig — ein **Zustand** der
 * Zeile und nicht die Merkmarke dessen, der sie zuletzt geöffnet hat. Was von
 * außen geht, ist der Inhalt einer Zelle, und der genügt.
 */

import { useEffect, useRef } from "react";
import type { ReactElement } from "react";

import { Icon } from "../design-system";
import { bringIntoView } from "./scrolling";

const TITLE = "Zuletzt geöffnet";

/**
 * `shown` entscheidet, ob die Zeile die Marke trägt. Die Zelle steht auch ohne
 * sie da — eine Spalte, die je nach Zeile verschwände, verschöbe die Tabelle.
 */
export function VisitedMarker({ shown }: { shown: boolean }): ReactElement {
  const mark = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    // `nearest`, damit eine ohnehin sichtbare Zeile die Liste nicht verrückt.
    //
    // Und über `bringIntoView` und nicht über `scrollIntoView`: Das verschöbe auch
    // die Hülle der Anwendung, die `overflow: hidden` trägt und die niemand
    // zurückscrollen kann — dann steht die ganze Oberfläche daneben (siehe
    // `scrolling.ts`).
    if (shown) {
      bringIntoView(mark.current, "nearest");
    }
  }, [shown]);

  return (
    <span
      ref={mark}
      title={shown ? TITLE : undefined}
      aria-label={shown ? TITLE : undefined}
      style={{ display: "flex", color: "var(--ac-blue)" }}
    >
      {shown ? <Icon name="chevron-right" size={14} /> : null}
    </span>
  );
}
